/* eslint-disable no-nested-ternary */
import * as React from 'react';
import useSWR from 'swr';
import type { AxiosError } from 'axios';
import axios from 'axios';
import {
  Box, createStyles, debounce, Grid, makeStyles, TextField, Button, CircularProgress, Tooltip,
} from '@material-ui/core';
import { useSnackbar } from 'notistack';
import Alert from '@material-ui/lab/Alert';
import { Autocomplete } from '@material-ui/lab';
import ImageIcon from '@material-ui/icons/Image';
import { useCookies } from 'react-cookie';
import { useNavigate, useParams } from 'react-router-dom';
import RepairCard from '../marking/RepairCard';
import GenericCard from '../global/GenericCard';
import useEventListener from '../../lib/useEventListener';
import deselectElementPreventDefaultAndBlur from '../../lib/deselectAndBlur';
import NavigationDropdown from '../navigation/NavigationDropdown';
import type IRepair from '../workReassignment/workReassignmentInterfaces';

const useStyles = makeStyles((theme) => createStyles({
  captureImageButton: {
    minWidth: '11rem',
    color: theme.palette.common.white,
    backgroundColor: theme.palette.primary.main,
    '&:hover': {
      backgroundColor: theme.palette.primary.light,
    },
  },
  jewelryImage: {
    maxWidth: '100%',
  },
}));

interface IPhotographyRepair {
  barcode:string,
  isHighPriority: boolean,
  photoBoxJewelryTypeEnum: 1 | 2 | 3 | 4 | 5 | 6,
  numberOfRepairItems: number
}

const Photography: React.FC = () => {
  // we store the booth the user selected as a cookie so it persists inbetween sessions
  const [cookies, setCookie] = useCookies<string, { boothNumber?:string, photographyNextPage?:string } >(['boothNumber', 'photographyNextPage']);

  // if the env file includes a photobox domain override and they are using booth 99, we use that so that we can use
  // the fjorge box on staging because of the destructive nature of the photography API (calling the API deletes the previous sessions photos)
  const photoBoxDomain = (
    (cookies.boothNumber === '99'
    && process.env.REACT_APP_PHOTOBOXDOMAIN_OVERRIDE
    && process.env.REACT_APP_PHOTOBOXDOMAIN_OVERRIDE !== '')
      ? process.env.REACT_APP_PHOTOBOXDOMAIN_OVERRIDE
      : process.env.REACT_APP_PHOTOBOXDOMAIN || '');

  // this detects if something is a ipv4
  const ipRegex = /\b(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\b/g;

  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [images, setImages] = React.useState<string[]>([]);
  const [uploadedImages, setUploadedImages] = React.useState<string[]>([]);
  const [captureAwaiting, setCaptureAwaiting] = React.useState<boolean>(false);
  const [uploadingImages, setUploadingImages] = React.useState<boolean>(false);
  const [barcode, setBarcode] = React.useState<string>();
  const [boothNumbers] = React.useState<Array<string> | undefined>(process.env.REACT_APP_PHOTOBOOTHS?.split(','));
  const [openRepairProcessing, setOpenRepairProcessing] = React.useState<boolean>(false);

  const navPages = ['Current', 'Estimating', 'Marking'];
  const navigate = useNavigate();

  const handlePhotoBoxChange = (boothNumberParam: string) => {
    setCookie('boothNumber', boothNumberParam, { path: '/', maxAge: 31540000 });
    setImages([]);
  };

  const handlePageSelectedChange = (page: string) => {
    setCookie('photographyNextPage', page);
  };

  const handleRoute = (barcodeRouteParam:string) => {
    switch (cookies.photographyNextPage) {
      case 'Current': {
        break;
      }
      case 'Estimating': {
        navigate(`/estimate-repair/${barcodeRouteParam}`);
        break;
      }
      case 'Marking': {
        navigate(`/mark-repair/${barcodeRouteParam}`);
        break;
      }
      default: {
        enqueueSnackbar(cookies.photographyNextPage ? `Invalid barcode action page selected: ${cookies.photographyNextPage}` : 'No next page action selected', { variant: 'error' });
        break;
      }
    }
  };

  const fetcher = async (url: string, barcodeParam: string) => {
    const res = await axios.get<IPhotographyRepair>(url, {
      params: {
        barcode: barcodeParam,
      },
    })
      .then((response) => response.data);
    setOpenRepairProcessing(false);
    return res;
  };

  const { data } = useSWR(barcode ? ['/photograph/getRepair', barcode] : null, (url, barcodeParam) => fetcher(url, barcodeParam), {
    revalidateOnFocus: false,
    revalidateOnMount: true,
    revalidateOnReconnect: false,
    refreshWhenOffline: false,
    refreshWhenHidden: false,
    refreshInterval: 0,
  });

  const clearState = () => {
    setImages([]);
    setUploadedImages([]);
    setBarcode(undefined);
  };

  // different enums result in different numbers of photos returning
  // it is helpful for retaking, and extra sets of photo to know how many photos
  // so we delete photos we no longer need out of state
  const photosPerType = () => {
    switch (data?.photoBoxJewelryTypeEnum) {
      case 1:
        return 4;
      case 2:
        return 4;
      case 3:
        return 11;
      case 4:
        return 4;
      case 5:
        return 2;
      case 6:
        return 3;
      default:
        return 4;
    }
  };

  // Below function will use WorkReassignment's GetRepair endpoint to verify and grab the repair detail and uses SetBarcode useState to save the validated barcode.
  const initialBarcodeScan = debounce((scannedBarcode: string) => {
    // if there's no content
    if (!scannedBarcode) {
      return;
    }
    if (scannedBarcode === data?.barcode) {
      return;
    }
    setOpenRepairProcessing(true);
    axios.post<IRepair>('/workReassignment/GetRepair', {
      RepairBarcode: scannedBarcode,
    })
      .then((response) => {
        setBarcode(response.data.barcode);
      })
      .catch((error: AxiosError) => {
        enqueueSnackbar(error?.response?.data, { persist: false, variant: 'error' });
        setOpenRepairProcessing(false);
      });
  }, 500);

  let barcodeBuffer = '';

  const handleOutScan = (scannedBarcode: string | '') => {
    axios.post('/scanner/LogManualScan', {
      ScanType: 'Out',
      ScanBody: scannedBarcode,
      Location: 'Photographer',
    })
      .catch((er: AxiosError) => {
        enqueueSnackbar(er?.response?.data, { persist: false, variant: 'error' });
      });
  };

  const handleBarcodeInput = (e: KeyboardEvent) => {
    if (e.key !== 'Shift') {
      barcodeBuffer = `${barcodeBuffer}${e.key}`;
      if (barcodeBuffer.toLowerCase().includes('in:')) {
        deselectElementPreventDefaultAndBlur(e);
        barcodeBuffer = '';
      }
      if (barcodeBuffer.includes('{*|}')) {
        deselectElementPreventDefaultAndBlur(e);
        if (cookies.photographyNextPage === 'Current' || data === undefined) {
          initialBarcodeScan(barcodeBuffer.replace('{*|}', ''));
        } else {
          handleRoute(barcodeBuffer.replace('{*|}', ''));
        }
        barcodeBuffer = '';
      }
      if (barcodeBuffer.toLowerCase().includes('out:')) {
        deselectElementPreventDefaultAndBlur(e);
        barcodeBuffer = '';
      }
      if (barcodeBuffer.includes('<>??<>')) {
        deselectElementPreventDefaultAndBlur(e);
        if (cookies.photographyNextPage === 'Current' || data === undefined) {
          handleOutScan(barcodeBuffer.replace('<>??<>', ''));
        } else {
          handleRoute(barcodeBuffer.replace('<>??<>', ''));
        }
        barcodeBuffer = '';
      }
    }
  };

  useEventListener('keydown', handleBarcodeInput);

  const { barcodeParam } = useParams();

  // if a barcode came in as a url query
  React.useEffect(() => {
    if (barcodeParam) {
      initialBarcodeScan(barcodeParam);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openNewTab = (url:string) => {
    const a = (window.open(url, '_blank'));
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions, @typescript-eslint/unbound-method
    a?.focus;
    return a;
  };

  const printRoutingCard = () => {
    axios.get<{ routingCardUrl:string }>('/photograph/getRoutingCardUrl', {
      params: {
        barcode,
      },
    })
      .then((response) => {
        openNewTab(response.data.routingCardUrl);
        setTimeout(() => { clearState(); }, 2500);
      })
      .catch((error: AxiosError): void => {
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        enqueueSnackbar(`Upload unsuccessful: ${error?.response?.data}`, { variant: 'error', autoHideDuration: 2500 });
      });
  };

  const sendPhoto = async (printCard = false) : Promise<string> => {
    if (!data) {
      enqueueSnackbar('Cannot upload photos without valid repair', { persist: false, variant: 'error' });
      throw new Error('Cannot upload photos without valid repair');
    }
    if (cookies.boothNumber === undefined) {
      enqueueSnackbar('Cannot upload photo without booth number selected!', { persist: false, variant: 'error' });
      throw new Error('Cannot upload photo without booth number selected!');
    }
    // have to stash this in a const otherwise typescript
    // says it might be undefined despite checking above
    const photoBoothNumber = cookies.boothNumber;

    setUploadingImages(true);

    // we need to filter out images we have already uploaded, because those images have been deleted
    // on the photobox and trying to upload them again will throw an error because the file is gone
    const filteredImages = images.filter((image) => !uploadedImages.includes(image));

    // we have to replace the ip address with the photoBoxDomain we are using iis to redirect so we have HTTPS throughout the process
    await axios.post('/photograph/AddPhoto', {
      photos: (filteredImages).map((element: string) => element.replace('http://', 'https://').replace(ipRegex, `pb${photoBoothNumber}.${photoBoxDomain}`)),
      barcode: data?.barcode,
    }).then(
      () => {
        setUploadedImages([...filteredImages, ...uploadedImages]);
        enqueueSnackbar('Upload successful', { variant: 'success' });
        if (printCard) {
          printRoutingCard();
        }
        setUploadingImages(false);
      },
    )
      .catch(
        (stuff:AxiosError) => {
          setUploadingImages(false);
          // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
          enqueueSnackbar(`Upload unsuccessful with the following message: ${stuff.response?.data || ''}`, { variant: 'error', persist: false });
        },
      );

    return 'done';
  };

  const capturePhoto = (redo = false) => {
    if (!data) {
      enqueueSnackbar('Cannot capture photo without valid repair', { persist: false, variant: 'error' });
      throw new Error('Cannot capture photo without valid repair');
    }
    if (cookies.boothNumber === undefined) {
      enqueueSnackbar('Cannot capture photo without booth number selected', { persist: false, variant: 'error' });
      throw new Error('Cannot capture photo without booth number selected');
    }
    const itemType = data.photoBoxJewelryTypeEnum;
    const itemId = data.barcode;
    const sessionType = data.photoBoxJewelryTypeEnum === 3 ? 2 : 1;
    enqueueSnackbar('Image capture started', { autoHideDuration: 2500 });
    setCaptureAwaiting(true);
    axios.get<{ BoothNumber:string, ImageList: string[], ItemID: string }>(
      `https://pb${cookies.boothNumber}.${photoBoxDomain}/capture_images/${cookies.boothNumber}/${itemType}/${itemId}/${sessionType}`,
      {
        timeout: 100 * 1000,
        // we are stripping out our auth headers because one we don't need them and they have potential to cause errors
        // and we don't want to send it where it isn't needed
        transformRequest: (returnData:IPhotographyRepair, headers) => {
          // @ts-expect-error: The type file for this is wrong.
          // eslint-disable-next-line  no-param-reassign, @typescript-eslint/no-unsafe-member-access
          delete headers.common.Authorization;
          return returnData;
        },
      },
    )
      .then(
        (res) => {
          setCaptureAwaiting(false);
          enqueueSnackbar('Capture successful!', { variant: 'success', autoHideDuration: 2500 });
          if (redo) {
            const numberOfPictures = photosPerType();
            const tempImages = images;
            for (let i = 0;
              // eslint-disable-next-line no-nested-ternary
              i < numberOfPictures;
              i += 1) {
              tempImages.pop();
            }
            setImages([...tempImages, ...res.data.ImageList]);
          } else {
            setImages([...images, ...res.data.ImageList]);
          }
        },
      )
      .catch((error: AxiosError) => {
        setCaptureAwaiting(false);
        enqueueSnackbar(error?.response?.data || 'Capture Photo API call failed.', { persist: false, variant: 'error' });
      });
  };

  const uploadPhotosAndTakeSecondSetOfPhotos = async () => {
    await sendPhoto(false).then(
      () => {
        capturePhoto();
      },
    );
  };

  const ImageMap = (
    images?.map((linkItem, linkItemIndex) => (
      <Grid item xs={4}>
        {cookies.boothNumber !== undefined
        && (
        <img
          className={classes.jewelryImage}
          // we have to replace the ip addresses because the photobox returns us an address with the ip instead of an address with https
          // and browsers will get upset if you try and fetch http resources while on an https connections
          src={linkItem.replace('http://', '').replace(ipRegex, `https://pb${cookies.boothNumber}.${photoBoxDomain}`)}
          alt={`Jewelry ${linkItemIndex + 1}`}
        />
        )}
      </Grid>
    ))
  );

  if (!data) {
    return (
      <Grid container direction="column" spacing={2} alignItems="center" justifyContent="center">
        <Grid item>
          <Alert severity="info">{'Scan in repair to start the photography process'}</Alert>
        </Grid>
        <Grid item>
          {openRepairProcessing
            ? (
              <CircularProgress />
            )
            : (
              <TextField
                variant="outlined"
                size="small"
                onKeyPress={
                (event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    event.stopPropagation();
                    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                    initialBarcodeScan((event.target as HTMLInputElement).value);
                  }
                }
                }
              />
            )}
        </Grid>
      </Grid>
    );
  }

  return (
    <Box>
      <Grid container spacing={0}>
        <Grid item xs={1} />
        <Grid item xs={8} container spacing={3}>
          { images.length === 0
            ? (
              <Grid item xs={12}>
                <Alert
                  severity="info"
                  action={
  (
    <>
      <Box mx={2}>
        <Button
          id="capture-images-button"
          variant="contained"
          color="inherit"
          className={classes.captureImageButton}
          onClick={() => capturePhoto()}
          disabled={captureAwaiting}
        >
          { captureAwaiting
            ? ('CAPTURING IMAGES...')
            : 'CAPTURE IMAGES'}
        </Button>
      </Box>
    </>
                )
              }
                >
                  {'Unpack repair item, place in photobox, then proceed to capture images.'}
                </Alert>
              </Grid>
            )
            : (
              <Grid item xs={12}>
                <Box mt={1} />
              </Grid>
            )}
          <Grid item xs={12} md={6}>
            <RepairCard
              barcode={data.barcode}
              error={data.isHighPriority}
              primaryText="Current Barcode"
              secondaryText={(data?.numberOfRepairItems > 1) ? `${data?.numberOfRepairItems} Items` : '1 Item'}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <GenericCard
              primaryText="Photo box being used"
              buttonContent={(
                <Box clone px={2}>
                  <Autocomplete
                    disabled={captureAwaiting || uploadingImages}
                    value={cookies.boothNumber}
                    onChange={(event: unknown, selectedBooth: string | null) => {
                      handlePhotoBoxChange(selectedBooth || '');
                    }}
                    autoHighlight
                    autoSelect
                    fullWidth
                    options={boothNumbers || new Array<string>()}
                    getOptionLabel={(option) => option}
                        // JAG MUI out of the box AutoComplete functionality requires prop spreading.
                        // eslint-disable-next-line react/jsx-props-no-spreading
                    renderInput={(params) => <TextField {...params} variant="outlined" />}
                  />
                </Box>
              )}
            >
              <Box py={0.5}>
                <ImageIcon style={{ fontSize: 26 }} />
              </Box>
            </GenericCard>
          </Grid>
          {ImageMap}
          <Grid item xs={12} />
          { images.length > 0
            && (
              <>
                <Grid item>
                  <Button
                    id="redo-batch-button"
                    color="secondary"
                    style={{ minWidth: '11rem' }}
                    variant="contained"
                    onClick={() => capturePhoto(true)}
                    disabled={captureAwaiting || uploadingImages || (data.photoBoxJewelryTypeEnum === 4 && images.length < 5 && uploadedImages.length > 0)}
                  >
                    { captureAwaiting
                      ? ('CAPTURING IMAGES...')
                      : 'REDO IMAGE BATCH'}
                  </Button>
                </Grid>
                <Grid item>
                  <Tooltip title="Upload images to the server">
                    <Button
                      id="accept-flip-images"
                      color="primary"
                      variant="contained"
                      style={{ minWidth: '27.5rem' }}
                      disabled={captureAwaiting || uploadingImages || uploadedImages.length > 0}
                      // eslint-disable-next-line no-void
                      onClick={() => { void uploadPhotosAndTakeSecondSetOfPhotos(); }}
                    >
                      { uploadingImages
                        ? ('UPLOADING IMAGES...') : captureAwaiting
                          ? ('CAPTURING IMAGES...')
                          : 'ACCEPT FIRST SET OF IMAGES & TAKE SECOND SET OF PHOTOS'}
                    </Button>
                  </Tooltip>
                </Grid>
                <Grid item>
                  <Tooltip
                    title={((data.photoBoxJewelryTypeEnum === 4 && images.length < 5) || !captureAwaiting || !uploadingImages)
                      ? 'Two sets of images are required for this image type'
                      : ''}
                  >
                    <span>
                      <Button
                        id="accept-images-button"
                        color="primary"
                        style={{ minWidth: '11rem' }}
                        variant="contained"
                        disabled={captureAwaiting || uploadingImages || (data.photoBoxJewelryTypeEnum === 4 && images.length < 5)}
                        onClick={() => sendPhoto(true)}
                      >
                        {
                        uploadingImages
                          ? ('UPLOADING IMAGES...') : captureAwaiting
                            ? ('CAPTURING IMAGES...')
                            : 'ACCEPT IMAGES & PRINT ROUTING CARD'
                        }
                      </Button>
                    </span>
                  </Tooltip>
                </Grid>
              </>
            )}
          <Grid item xs={12} />
          <Grid item>
            <NavigationDropdown
              pageSelected={cookies.photographyNextPage}
              pages={navPages}
              handlePageChange={handlePageSelectedChange}
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={3} />

    </Box>
  );
};
export default Photography;
